<template>
  <div class="card border-translucent">
    <div class="card-header d-flex align-items-center gap-2 py-2">
      <div class="min-w-0 me-auto">
        <div class="d-flex flex-wrap align-items-baseline column-gap-3">
          <button
            v-if="source.statement_upload_id !== null"
            type="button"
            class="btn btn-link p-0 fw-semibold text-start text-break"
            :title="t('ecolabels.labels.source.download')"
            @click="downloadDocument"
          >
            <font-awesome-icon icon="download" class="me-1" />
            {{ source.file_name }}
          </button>
          <span v-else class="fw-semibold text-break">
            {{ source.file_name }}
          </span>
          <span v-if="!expanded" class="small text-body-secondary">
            {{ summary }}
          </span>
        </div>
        <div
          v-if="expanded && source.seller_company_name"
          class="small text-body-secondary"
        >
          {{
            t("ecolabels.labels.source.seller", {
              company: source.seller_company_name,
            })
          }}
        </div>
      </div>
      <span v-if="source.problems.length > 0" class="badge pill-attention">
        {{ t("ecolabels.labels.source.needs_attention") }}
      </span>
      <button
        v-if="editable"
        type="button"
        class="btn btn-sm btn-phoenix-secondary text-nowrap"
        :disabled="busy"
        @click="$emit('remove')"
      >
        <font-awesome-icon icon="trash" class="me-1" />
        {{ t("ecolabels.labels.source.remove") }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-phoenix-secondary"
        :aria-expanded="expanded"
        :aria-controls="ids.body"
        :aria-label="toggleText"
        :title="toggleText"
        @click="expanded = !expanded"
      >
        <font-awesome-icon :icon="expanded ? 'chevron-up' : 'chevron-down'" />
      </button>
    </div>

    <div v-show="expanded" :id="ids.body" class="card-body">
      <div v-if="editable" class="row g-2 mb-3">
        <div class="col-12">
          <label :for="ids.beneficiaryName" class="form-label">
            {{ t("ecolabels.labels.source.beneficiary_name") }}
          </label>
          <input
            :id="ids.beneficiaryName"
            v-model="beneficiaryName"
            class="form-control form-control-sm"
            maxlength="256"
            @change="scheduleSave"
          />
        </div>
        <div class="col-md-6">
          <label :for="ids.beneficiaryCountry" class="form-label">
            {{ t("ecolabels.labels.source.beneficiary_country") }}
          </label>
          <select
            :id="ids.beneficiaryCountry"
            v-model="beneficiaryCountry"
            class="form-select form-select-sm"
            @change="scheduleSave"
          >
            <option value="" />
            <option
              v-for="option in beneficiaryCountryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label :for="ids.registryCountry" class="form-label">
            {{ t("ecolabels.labels.source.registry_country") }}
          </label>
          <select
            :id="ids.registryCountry"
            v-model="registryCountry"
            class="form-select form-select-sm"
            @change="scheduleSave"
          >
            <option value="" />
            <option
              v-for="option in registryCountryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label :for="ids.registryCode" class="form-label">
            {{ t("ecolabels.labels.source.registry_code") }}
          </label>
          <input
            :id="ids.registryCode"
            v-model="registryCode"
            class="form-control form-control-sm"
            maxlength="64"
            @change="scheduleSave"
          />
        </div>
        <div class="col-md-6">
          <label :for="ids.total" class="form-label">
            {{ t("ecolabels.labels.source.total_mwh") }}
          </label>
          <input
            :id="ids.total"
            v-model="totalText"
            class="form-control form-control-sm"
            :class="{ 'is-invalid': totalInvalid }"
            type="number"
            inputmode="numeric"
            min="0"
            step="1"
            @change="scheduleSave"
          />
          <div v-if="totalInvalid" class="invalid-feedback">
            {{ t("ecolabels.labels.source.total_invalid") }}
          </div>
        </div>
        <div class="col-md-6">
          <label :for="ids.periodStart" class="form-label">
            {{ t("ecolabels.labels.source.period_start") }}
          </label>
          <DatePicker
            :modelValue="toDate(periodStart)"
            :inputId="ids.periodStart"
            :manualInput="false"
            dateFormat="dd/mm/yy"
            showButtonBar
            appendTo="body"
            size="small"
            class="w-100"
            :invalid="periodReversed"
            @update:modelValue="setPeriodStart"
          />
        </div>
        <div class="col-md-6">
          <label :for="ids.periodEnd" class="form-label">
            {{ t("ecolabels.labels.source.period_end") }}
          </label>
          <DatePicker
            :modelValue="toDate(periodEnd)"
            :inputId="ids.periodEnd"
            :manualInput="false"
            dateFormat="dd/mm/yy"
            showButtonBar
            appendTo="body"
            size="small"
            class="w-100"
            :invalid="periodReversed"
            @update:modelValue="setPeriodEnd"
          />
        </div>
        <div v-if="periodReversed" class="col-12">
          <div class="invalid-feedback d-block mt-0">
            {{ t("ecolabels.labels.detail.period_reversed") }}
          </div>
        </div>
      </div>

      <dl v-else class="row small mb-3">
        <template v-for="field in readOnlyFields" :key="field.key">
          <dt class="col-sm-5 fw-normal text-body-secondary">
            {{ t(field.key) }}
          </dt>
          <dd class="col-sm-7 mb-1 text-break">{{ field.value || "-" }}</dd>
        </template>
      </dl>

      <LabelSourceDevicesComponent
        :labelId
        :source
        :editable
        :catalog
        :catalogLoading
        @changed="$emit('changed')"
      />

      <ul
        v-if="source.problems.length > 0"
        class="text-warning-emphasis small mb-0 mt-2 ps-3"
      >
        <li v-for="(problem, index) in source.problems" :key="index">
          {{ problemText(problem) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SUPPORTED_REGISTRIES_WITH_LABELS } from "@/constants";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { useServerDraft } from "@/composables/useServerDraft";
import {
  getRegistryLabel,
  normalizeRegistryCountryRegion,
} from "@/utils/registries";
import { useDebounceFn } from "@vueuse/core";
import { compact, isEqual, sortBy } from "lodash-es";
import DatePicker from "primevue/datepicker";
import { computed, provide, ref, useId, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type {
  EcolabelDevice,
  UpdateStatementRequest,
} from "../api/ecolabelsTypes";
import {
  isoCountryOptions,
  withUnknownCodes,
  type CountryOption,
} from "../countryOptions";
import LabelSourceDevicesComponent from "./LabelSourceDevicesComponent.vue";
import {
  formatDateRange,
  isWholeMwh,
  labelProblemMessage,
  mwhOf,
  pickedIsoDate,
  type PickedDate,
} from "./labelDisplay";
import type { LabelProblem, LabelSource } from "./labelsTypes";
import { sourceWritesKey, useBusyAction } from "./useBusyAction";

type TextField =
  | "beneficiary_name"
  | "beneficiary_country"
  | "registry_code"
  | "period_start"
  | "period_end";

const SAVE_DELAY_MS = 300;

const props = withDefaults(
  defineProps<{
    labelId: number;
    source: LabelSource;
    editable: boolean;
    defaultExpanded?: boolean;
    catalog?: EcolabelDevice[];
    catalogLoading?: boolean;
  }>(),
  { defaultExpanded: false, catalog: () => [], catalogLoading: false }
);

const emit = defineEmits<{
  changed: [];
  remove: [];
  "update:pending": [pending: boolean];
}>();

const { t } = useI18n();
const { downloadCompanyDocument } = useFileDownload();
const ids = {
  beneficiaryName: useId(),
  beneficiaryCountry: useId(),
  registryCountry: useId(),
  registryCode: useId(),
  total: useId(),
  periodStart: useId(),
  periodEnd: useId(),
  body: useId(),
};

const writes = useBusyAction();
const { busy, run } = writes;
const beneficiaryName = textDraft("beneficiary_name");
const beneficiaryCountry = textDraft("beneficiary_country");
const registryCode = textDraft("registry_code");
const periodStart = textDraft("period_start");
const periodEnd = textDraft("period_end");
const registryCountry = useServerDraft(
  () => ({ id: props.source.id, value: props.source.country }),
  ({ value }) => normalizeRegistryCountryRegion(value) ?? value ?? ""
);
const totalText = useServerDraft(
  () => ({ id: props.source.id, value: props.source.total_mwh }),
  ({ value }) => (value === null ? "" : String(value))
);
const expanded = ref(props.defaultExpanded);
const saveScheduled = ref(false);

const pending = computed(() => saveScheduled.value || busy.value);
const totalInvalid = computed(() => !isWholeMwh(totalText.value));
const periodReversed = computed(
  () =>
    periodStart.value !== "" &&
    periodEnd.value !== "" &&
    periodStart.value > periodEnd.value
);
const beneficiaryCountryOptions = computed(() =>
  withUnknownCodes(
    // NOTE: the backend validates the code with pycountry, which has no Kosovo code XK.
    isoCountryOptions().filter((option) => option.value !== "XK"),
    [beneficiaryCountry.value]
  )
);
const registryCountryOptions = computed(() =>
  withCurrentValue(registryAndCountryOptions(), registryCountry.value)
);
const readOnlyFields = computed(() => [
  {
    key: "ecolabels.labels.source.beneficiary_name",
    value: props.source.beneficiary_name,
  },
  {
    key: "ecolabels.labels.source.beneficiary_country",
    value: props.source.beneficiary_country,
  },
  {
    key: "ecolabels.labels.source.registry_country",
    value: props.source.country,
  },
  {
    key: "ecolabels.labels.source.registry_code",
    value: props.source.registry_code,
  },
  {
    key: "ecolabels.labels.source.total_mwh",
    value:
      props.source.total_mwh === null
        ? null
        : formatNumberWithSpaces(props.source.total_mwh),
  },
  {
    key: "ecolabels.labels.source.period",
    value: formatDateRange(props.source.period_start, props.source.period_end),
  },
]);
const summary = computed(() =>
  compact([
    props.source.beneficiary_name,
    props.source.beneficiary_country,
    props.source.total_mwh !== null &&
      `${formatNumberWithSpaces(props.source.total_mwh)} ${t("common.mwh")}`,
    formatDateRange(props.source.period_start, props.source.period_end),
    t("ecolabels.labels.source.device_count", props.source.devices.length),
  ]).join(" · ")
);
const toggleText = computed(() =>
  t(
    expanded.value
      ? "ecolabels.labels.source.collapse"
      : "ecolabels.labels.source.expand"
  )
);

const debouncedSave = useDebounceFn(() => {
  saveScheduled.value = false;
  return run(saveFields);
}, SAVE_DELAY_MS);

watch(pending, (value) => emit("update:pending", value));

function scheduleSave() {
  saveScheduled.value = true;
  void debouncedSave();
}

function textDraft(field: TextField): Ref<string> {
  return useServerDraft(
    () => ({ id: props.source.id, value: props.source[field] }),
    ({ value }) => value ?? ""
  );
}

function setPeriodStart(date: PickedDate) {
  periodStart.value = pickedIsoDate(date);
  scheduleSave();
}

function setPeriodEnd(date: PickedDate) {
  periodEnd.value = pickedIsoDate(date);
  scheduleSave();
}

async function saveFields() {
  if (totalInvalid.value || periodReversed.value) return;
  const request = statementRequest();
  if (isEqual(request, savedStatementRequest())) return;
  await ecolabelsApi.updateStatement(props.source.id, request);
  emit("changed");
}

async function downloadDocument() {
  if (props.source.statement_upload_id === null) return;
  await downloadCompanyDocument(
    props.source.company_id,
    props.source.statement_upload_id,
    { filename: props.source.file_name }
  );
}

function statementRequest(): UpdateStatementRequest {
  return {
    company_id: props.source.company_id,
    beneficiary_name: blankToNull(beneficiaryName.value),
    beneficiary_country: blankToNull(beneficiaryCountry.value),
    registry_code: blankToNull(registryCode.value),
    country: blankToNull(registryCountry.value),
    period_start: blankToNull(periodStart.value),
    period_end: blankToNull(periodEnd.value),
    total_mwh: mwhOf(totalText.value),
    notes: props.source.notes,
  };
}

function savedStatementRequest(): UpdateStatementRequest {
  const { source } = props;
  return {
    company_id: source.company_id,
    beneficiary_name: source.beneficiary_name,
    beneficiary_country: source.beneficiary_country,
    registry_code: source.registry_code,
    country: source.country,
    period_start: source.period_start,
    period_end: source.period_end,
    total_mwh: source.total_mwh,
    notes: source.notes,
  };
}

function problemText(problem: LabelProblem): string {
  const { key, params } = labelProblemMessage(problem);
  return t(key, params);
}

function registryAndCountryOptions(): CountryOption[] {
  const registries = SUPPORTED_REGISTRIES_WITH_LABELS.map((registry) => ({
    value: registry.value,
    label: getRegistryLabel(registry.value, t),
  }));
  const registryValues = new Set(registries.map((option) => option.value));
  const otherCountries = isoCountryOptions().filter(
    (option) => !registryValues.has(option.value)
  );
  return sortBy([...registries, ...otherCountries], "label");
}

function withCurrentValue(
  options: CountryOption[],
  value: string
): CountryOption[] {
  if (value === "" || options.some((option) => option.value === value))
    return options;
  return [{ value, label: value }, ...options];
}

function blankToNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === "" ? null : trimmed;
}

function toDate(isoDate: string): Date | null {
  return isoDate === "" ? null : new Date(`${isoDate}T00:00:00`);
}

provide(sourceWritesKey, writes);
</script>
